import { Button, Card, CardContent, Typography } from '@eye-able/ui';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useFetchPolicy } from '@http/queries/useFetchPolicy';
import { useUpdatePolicy } from '@http/queries/useUpdatePolicy';
import LoadingSpinner from '@components/LoadingSpinner';
import PolicyForm, { policyToInitialValues } from './PolicyForm';
import { parsePolicyId } from './policyId';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';

function EditPolicyStatusCard({ children }: { children: string }) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:flex-col eye:gap-s'>
      <Button asChild variant='ghost' size='sm' className='eye:self-start'>
        <RouterLink to='/policies'>
          <ArrowLeft aria-hidden />
          {t('backToPolicies')}
        </RouterLink>
      </Button>
      <Card>
        <CardContent className='eye:p-s'>
          <Typography className='eye:text-grey-120'>{children}</Typography>
        </CardContent>
      </Card>
    </div>
  );
}

function EditPolicyContent() {
  const { t } = useInlineTranslation();
  const navigate = useNavigate();
  const { policyId } = useParams();
  const id = parsePolicyId(policyId);

  const { data: policy, isLoading, isError } = useFetchPolicy(id);
  const updatePolicy = useUpdatePolicy();

  const initialValues = useMemo(
    () => (policy ? policyToInitialValues(policy) : null),
    [policy],
  );

  if (id === null) {
    return <EditPolicyStatusCard>{t('policyNotFound')}</EditPolicyStatusCard>;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !policy || !initialValues) {
    return (
      <EditPolicyStatusCard>{t('couldNotLoadPolicy')}</EditPolicyStatusCard>
    );
  }

  return (
    <PolicyForm
      heading={t('editPolicy')}
      description={t('editPolicyDescription')}
      submitLabel={t('saveChanges')}
      canSaveAsDraft={false}
      isSubmitting={updatePolicy.isPending}
      initialValues={initialValues}
      onSubmit={(body) =>
        updatePolicy.mutate(
          { ...body, id },
          { onSuccess: () => navigate('/policies') },
        )
      }
    />
  );
}

function EditPolicy() {
  return (
    <GovernanceAccessBoundary area='customPolicies' requiredLevel='editor'>
      <EditPolicyContent />
    </GovernanceAccessBoundary>
  );
}

export { EditPolicy as Component };
export default EditPolicy;
